import { PERSONAL_AREA_NAME, DEFAULT_LANG } from '@/config/constants'

const ABSOLUTE_URL_REGEX = /^(?:[a-z+]+:)?\/\//i

/**
 * Checks if a URL is absolute (starts with protocol or //)
 */
export const isAbsoluteUrl = (url?: string): boolean => {
  if (!url) return false
  return ABSOLUTE_URL_REGEX.test(url)
}

/**
 * Returns the external API URL based on the environment
 *
 * @returns {string} The external API URL
 */
export const getExternalApiUrl = () => {
  // For production, use the configured DMS API URL
  const dmsApiUrl =
    process.env.NEXT_PUBLIC_DMS_API_URL || process.env.DMS_API_URL
  if (dmsApiUrl) {
    return dmsApiUrl
  }

  // If no external API is configured, use the internal API
  // This will be useful during the transition period
  if (typeof window === 'undefined') {
    return `${process.env.NEXT_PUBLIC_BASE_URL || process.env.VERCEL_URL || 'http://localhost:3000'}/api/test`
  }
  return '/api/test'
}

export const getFormattedUrl = (
  endpoint: string,
  params?: { [key: string]: string }
) => {
  const pars = Object.entries(params || {})

  if (pars.length === 0) return endpoint

  pars.forEach(([key, value]) => {
    endpoint = endpoint.replace(`{${key}}`, value)
  })

  return endpoint
}

export const getApplicationHref = (
  applicationId: number,
  type: 'competition' | 'application' = 'competition'
) => {
  const base = `/${PERSONAL_AREA_NAME}/${applicationId}` // id: is the id of the user application
  return type === 'application' ? `${base}?type=application` : base
}

export const buildQueryString = (
  endpoint: string,
  params?: { [key: string]: string }
) => {
  if (!params || Object.keys(params).length === 0) return endpoint

  // Check if endpoint already has a query string
  const [baseUrl, existingQuery] = endpoint.split('?')

  // Parse existing query parameters
  const existingParams = new Map<string, string>()
  if (existingQuery) {
    existingQuery.split('&').forEach((param) => {
      const [key, value] = param.split('=')
      if (key) existingParams.set(key, value || '')
    })
  }

  // Merge params - new params override existing ones
  Object.entries(params).forEach(([key, value]) => {
    existingParams.set(key, value)
  })

  // Build the final query string
  const queryString = Array.from(existingParams.entries())
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join('&')

  return `${baseUrl}?${queryString}`
}

export const getLocaleUrl = (url: string, locale = DEFAULT_LANG) => {
  return locale === DEFAULT_LANG ? url : `/${locale}${url}`
}

export const buildApplyLink = (
  slug: string,
  locale = DEFAULT_LANG,
  type: 'competition' | 'application' = 'competition'
) => {
  if (!slug) return null
  const base = getLocaleUrl(`/${PERSONAL_AREA_NAME}/apply/${slug}`, locale)
  return type === 'application' ? `${base}?type=application` : base
}
